import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle, ArrowRight, Wrench } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FeatureList = ({ features }) => {
  if (!features || features.length === 0) return null;
  
  return (
    <div className="grid grid-cols-2 gap-4 mb-8">
      {features.map((feature, fIdx) => (
        <div key={fIdx} className="flex items-center space-x-2">
          <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
          <span className="text-slate-300 text-sm">{feature}</span>
        </div>
      ))}
    </div>
  );
};

const ServiceItem = ({ service, idx }) => {
  const isReversed = idx % 2 !== 0;
  
  return (
    <div 
      data-testid={`service-detail-${service.id}`}
      className="grid lg:grid-cols-2 gap-12 items-center"
    >
      <div className={isReversed ? 'lg:order-2' : ''}>
        <div className="flex items-center space-x-4 mb-6">
          <div className="w-14 h-14 bg-blue-600/20 rounded-sm flex items-center justify-center">
            <Wrench className="w-7 h-7 text-blue-500" />
          </div>
          <span className="text-blue-400 uppercase tracking-widest text-sm">
            Service {String(idx + 1).padStart(2, '0')}
          </span>
        </div>
        <h2 
          className="text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight mb-6"
          style={{ fontFamily: 'var(--font-heading)' }}
        >
          {service.title}
        </h2>
        <p className="text-slate-400 mb-8 leading-relaxed">
          {service.description}
        </p>
        <FeatureList features={service.features} />
        <Link to="/contact">
          <Button 
            data-testid={`service-enquiry-btn-${service.id}`}
            className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm uppercase tracking-wider"
          >
            Enquire About This Service
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </Link>
      </div>
      <div className={isReversed ? 'lg:order-1' : ''}>
        <div className="relative overflow-hidden rounded-sm">
          <img
            src={service.image_url}
            alt={service.title}
            className="w-full h-[400px] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 to-transparent" />
        </div>
      </div>
    </div>
  );
};

const ServicesPage = () => {
  const [services, setServices] = useState([]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error("Error fetching services:", error);
      }
    };
    fetchServices();
  }, []);

  return (
    <div data-testid="services-page" className="pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://customer-assets.emergentagent.com/job_68d20dfb-0764-4836-bf84-01b37bd0406f/artifacts/6s9gtdyf_cnc-laser-cutting-of-metal-modern-industrial-tech-2023-02-01-19-21-50-utc.jpg"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-blue-400 uppercase tracking-widest text-sm mb-4">
              Our Services
            </p>
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white uppercase tracking-tight mb-6"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              Metal Fabrication <span className="text-blue-500">Solutions</span>
            </h1>
            <p className="text-slate-300 text-lg max-w-3xl mx-auto">
              Nassau Engineering LTD provides a comprehensive range of fabrication services 
              designed to meet the most demanding project requirements.
            </p>
          </div>
        </div>
      </section>

      {/* Services Detail Section */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-24">
            {services.map((service, idx) => (
              <ServiceItem key={service.id} service={service} idx={idx} />
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-24 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-blue-400 uppercase tracking-widest text-sm mb-4">
              Additional Capabilities
            </p>
            <h2 
              className="text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight"
              style={{ fontFamily: 'var(--font-heading)' }}
            >
              More <span className="text-blue-500">Services</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Card 
              data-testid="additional-service-0"
              className="bg-slate-950 border-slate-800 rounded-sm card-hover"
            >
              <CardContent className="p-6">
                <h3 
                  className="text-lg font-bold text-white uppercase tracking-tight mb-3"
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  Structural Steel
                </h3>
                <p className="text-slate-400 text-sm">
                  Complete structural steel solutions for construction and infrastructure projects.
                </p>
              </CardContent>
            </Card>
            <Card 
              data-testid="additional-service-1"
              className="bg-slate-950 border-slate-800 rounded-sm card-hover"
            >
              <CardContent className="p-6">
                <h3 
                  className="text-lg font-bold text-white uppercase tracking-tight mb-3"
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  Custom Fabrication
                </h3>
                <p className="text-slate-400 text-sm">
                  Bespoke metalwork solutions tailored to your specific requirements.
                </p>
              </CardContent>
            </Card>
            <Card 
              data-testid="additional-service-2"
              className="bg-slate-950 border-slate-800 rounded-sm card-hover"
            >
              <CardContent className="p-6">
                <h3 
                  className="text-lg font-bold text-white uppercase tracking-tight mb-3"
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  Installation Services
                </h3>
                <p className="text-slate-400 text-sm">
                  Professional installation and fitting services for all fabricated components.
                </p>
              </CardContent>
            </Card>
            <Card 
              data-testid="additional-service-3"
              className="bg-slate-950 border-slate-800 rounded-sm card-hover"
            >
              <CardContent className="p-6">
                <h3 
                  className="text-lg font-bold text-white uppercase tracking-tight mb-3"
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  Design Consultation
                </h3>
                <p className="text-slate-400 text-sm">
                  Expert engineering consultation to optimize your project design.
                </p>
              </CardContent>
            </Card>
            <Card 
              data-testid="additional-service-4"
              className="bg-slate-950 border-slate-800 rounded-sm card-hover"
            >
              <CardContent className="p-6">
                <h3 
                  className="text-lg font-bold text-white uppercase tracking-tight mb-3"
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  Project Management
                </h3>
                <p className="text-slate-400 text-sm">
                  End-to-end project management ensuring on-time delivery.
                </p>
              </CardContent>
            </Card>
            <Card 
              data-testid="additional-service-5"
              className="bg-slate-950 border-slate-800 rounded-sm card-hover"
            >
              <CardContent className="p-6">
                <h3 
                  className="text-lg font-bold text-white uppercase tracking-tight mb-3"
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  Maintenance & Repair
                </h3>
                <p className="text-slate-400 text-sm">
                  Ongoing maintenance and repair services for existing installations.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight mb-6"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            Need a Custom Solution?
          </h2>
          <p className="text-blue-100 text-lg mb-8">
            Our team is ready to discuss your specific requirements and provide a tailored solution 
            for your project. Get a free estimate within 24 hours.
          </p>
          <Link to="/contact">
            <Button 
              data-testid="services-cta-btn"
              size="lg"
              className="bg-white text-blue-600 hover:bg-slate-100 rounded-sm uppercase tracking-wider"
            >
              Request a Quote
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
